import { useEffect, useState } from 'react';
import { timeApi } from '../../../api/time.js';
import { hrApi } from '../../../api/hr.js';

/**
 * Work schedule admin — list/create schedules (per-weekday hours map) and assign
 * a schedule to an employee.
 */
const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export default function SchedulesPage() {
  const [schedules, setSchedules] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  function refresh() {
    setLoading(true);
    Promise.all([
      timeApi.listSchedules(),
      timeApi.listAssignments(),
      hrApi.listEmployees().catch(() => ({ items: [] })),
    ])
      .then(([s, a, e]) => {
        setSchedules(s?.items || []);
        setAssignments(a?.items || []);
        setEmployees(e?.items || e || []);
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed to load schedules'))
      .finally(() => setLoading(false));
  }
  useEffect(refresh, []);

  async function createSchedule(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const days = {};
    for (const d of WEEKDAYS) {
      const raw = fd.get(`day-${d}`);
      if (raw !== '' && raw != null) days[d] = Number(raw);
    }
    try {
      await timeApi.createSchedule({
        name: fd.get('name'),
        timezone: fd.get('timezone') || 'UTC',
        days,
        effectiveFrom: fd.get('effectiveFrom') || null,
      });
      e.target.reset();
      setShowCreate(false);
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create schedule');
    }
  }

  async function assign(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await timeApi.assignSchedule({
        employeeId: fd.get('employeeId'),
        scheduleId: fd.get('scheduleId'),
        effectiveFrom: fd.get('effectiveFrom') || null,
      });
      e.target.reset();
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign schedule');
    }
  }

  async function removeSchedule(id) {
    try {
      await timeApi.deleteSchedule(id);
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete schedule');
    }
  }

  async function unassign(id) {
    try {
      await timeApi.unassignSchedule(id);
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove assignment');
    }
  }

  function dayLabel(days) {
    const d = typeof days === 'string' ? JSON.parse(days || '{}') : days || {};
    const total = Object.values(d).reduce((sum, h) => sum + Number(h || 0), 0);
    return `${total}h / week`;
  }

  return (
    <section data-testid="time-schedules-page" data-loading={loading ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Work Schedules</h1>
          <p className="muted">{schedules.length} schedules</p>
        </div>
        <button className="btn primary" data-testid="time-schedule-create" onClick={() => setShowCreate((s) => !s)}>
          {showCreate ? 'Close' : '+ Schedule'}
        </button>
      </header>

      {error && <div className="alert error" data-testid="time-schedules-error">{error}</div>}

      {showCreate && (
        <form onSubmit={createSchedule} className="card form-card grid-form">
          <label>Name<input name="name" data-testid="time-schedule-name-input" required /></label>
          <label>Timezone<input name="timezone" defaultValue="Asia/Jakarta" data-testid="time-schedule-timezone-input" /></label>
          <label>Effective from<input name="effectiveFrom" type="date" data-testid="time-schedule-effectivefrom-input" /></label>
          {WEEKDAYS.map((d) => (
            <label key={d}>{d.toUpperCase()} hrs
              <input name={`day-${d}`} type="number" min="0" max="24" step="0.5" defaultValue={d === 'sat' || d === 'sun' ? 0 : 8} data-testid={`time-schedule-day-${d}-input`} />
            </label>
          ))}
          <div className="full actions"><button className="btn primary" type="submit" data-testid="time-schedule-submit">Create schedule</button></div>
        </form>
      )}

      <div className="card">
        <table className="data-table" data-testid="time-schedule-list">
          <thead><tr><th>Name</th><th>Timezone</th><th>Hours</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {schedules.map((s) => (
              <tr key={s.id} data-testid={`time-schedule-row-${s.id}`}>
                <td>{s.name}</td>
                <td>{s.timezone}</td>
                <td>{dayLabel(s.days)}</td>
                <td>{s.active ? 'yes' : 'no'}</td>
                <td><button className="btn ghost" data-testid={`time-schedule-delete-${s.id}`} onClick={() => removeSchedule(s.id)}>Delete</button></td>
              </tr>
            ))}
            {!loading && schedules.length === 0 && <tr><td colSpan={5} className="muted">No schedules yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3>Assign schedule</h3>
        <form onSubmit={assign} className="inline-form">
          <select name="employeeId" data-testid="time-assign-employee-input" required defaultValue="">
            <option value="" disabled>— employee —</option>
            {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.user_name || emp.employee_code || emp.id}</option>)}
          </select>
          <select name="scheduleId" data-testid="time-assign-schedule-input" required defaultValue="">
            <option value="" disabled>— schedule —</option>
            {schedules.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input name="effectiveFrom" type="date" data-testid="time-assign-effectivefrom-input" />
          <button className="btn primary" type="submit" data-testid="time-assign-submit">Assign</button>
        </form>
        <table className="data-table" data-testid="time-assignment-list">
          <thead><tr><th>Employee</th><th>Schedule</th><th>Effective from</th><th></th></tr></thead>
          <tbody>
            {assignments.map((a) => (
              <tr key={a.id} data-testid={`time-assignment-row-${a.id}`}>
                <td>{a.employee_name || a.employee_code || a.employee_id}</td>
                <td>{a.schedule_name}</td>
                <td>{a.effective_from ? String(a.effective_from).slice(0, 10) : '—'}</td>
                <td><button className="btn ghost" data-testid={`time-assignment-remove-${a.id}`} onClick={() => unassign(a.id)}>Remove</button></td>
              </tr>
            ))}
            {!loading && assignments.length === 0 && <tr><td colSpan={4} className="muted">No assignments yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
